import { colorToHex, type Cmyk, type Color, type Paint } from '@flobi/artwork-core';

/** A hex colour as the engine wants it: channels from 0 to 1. */
export function hexColor(hex: string): Color {
  const value = parseInt(hex.slice(1), 16);
  return { r: ((value >> 16) & 255) / 255, g: ((value >> 8) & 255) / 255, b: (value & 255) / 255, a: 1 };
}

/** A hex colour with the ink it stands for in a CMYK file, when there is one. */
export function inkedColor(hex: string, ink: Cmyk | undefined): Color {
  const color = hexColor(hex);
  return ink ? { ...color, cmyk: { ...ink } } : color;
}

/** What a tool draws with: the style's fill and stroke, with their ink in a CMYK file. */
export const styleFill = (style: { fill: string; fillInk?: Cmyk }): Color => inkedColor(style.fill, style.fillInk);
export const styleStroke = (style: { stroke: string; strokeInk?: Cmyk }): Color => inkedColor(style.stroke, style.strokeInk);

/**
 * What a node is actually wearing: its fill, its outline and how thick that outline is.
 *
 * One reading shared by the rail's two swatches and the restyle that changes them, so
 * the swatch and the shape cannot disagree. They used to, and a swatch showing a colour
 * the shape is not wearing makes everything you pick next look like it overwrote
 * something.
 *
 * Null means that half is not showing, which is a different answer from black.
 */
export function paintOf(node: unknown): { fill: string | null; stroke: string | null; weight: number } {
  const worn = node as
    | { fills?: Paint[]; strokes?: Paint[]; strokeStyle?: { weight?: number } }
    | null
    | undefined;
  const weight = worn?.strokeStyle?.weight ?? 0;
  return {
    fill: solidOf(worn?.fills),
    // An outline with no weight is not an outline, whatever colour is stored with it.
    stroke: weight > 0 ? solidOf(worn?.strokes) : null,
    weight,
  };
}

/** The first solid paint's colour, which is the one a swatch can show. */
function solidOf(paints: Paint[] | undefined): string | null {
  const paint = (paints ?? []).find((item) => item.visible !== false && item.type === 'solid');
  return paint && paint.type === 'solid' ? colorToHex(paint.color) : null;
}
